import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_RANGE_VALUE_REGEX, PERIOD_DATE_FORMAT, PERIOD_RANGE_SEPARATOR } from '../constants/periodValue.constant';
import type { DateRangeType, PeriodValueType } from '../types/period.type';
import { isPeriodPresetOption } from './period.utils';

dayjs.extend(customParseFormat);

/**
 * Reads an explicit `YYYY/MM/DD to YYYY/MM/DD` range, or null when the string is not one.
 */
export function parseDateRange(raw: string): DateRangeType | null {
  if (!DATE_RANGE_VALUE_REGEX.test(raw)) {
    return null;
  }

  const [startRaw, endRaw] = raw.split(PERIOD_RANGE_SEPARATOR);
  const start = dayjs(startRaw, PERIOD_DATE_FORMAT, true).startOf('day');
  const end = dayjs(endRaw, PERIOD_DATE_FORMAT, true).endOf('day');

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return null;
  }

  return { start, end };
}

/**
 * Reads a raw `period` field value as either a preset key or an explicit range.
 */
export function parsePeriodValue(raw: unknown): PeriodValueType | null {
  if (typeof raw !== 'string' || raw === '') {
    return null;
  }

  if (isPeriodPresetOption(raw)) {
    return { kind: 'preset', option: raw };
  }

  const range = parseDateRange(raw);

  return range ? { kind: 'custom', range } : null;
}

/**
 * Writes a range back to the `YYYY/MM/DD to YYYY/MM/DD` storage format.
 */
export function serializeDateRange({ start, end }: DateRangeType): string {
  return `${start.format(PERIOD_DATE_FORMAT)}${PERIOD_RANGE_SEPARATOR}${end.format(PERIOD_DATE_FORMAT)}`;
}

/**
 * Writes a period value back to the string stored in the form: a preset key or a range.
 */
export function serializePeriodValue(value: PeriodValueType): string {
  return value.kind === 'preset' ? value.option : serializeDateRange(value.range);
}
